import AdvanceTable from "components/common/advance-table/AdvanceTable";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Col, Dropdown, Row, Spinner } from "react-bootstrap";
import useApiKeys from "hooks/apikeys/useApiKeys";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import CardDropdown from "components/common/CardDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useFormattedDate from "hooks/useFormatDate";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import { Empty, Input, Select } from "antd";
import { ApiKeyDetailsDrawer } from "./ApiKeyDetailsDrawer";

export const ApiKeys = () => {
  const token = sessionStorage.getItem("authToken");

  const {
    apiKeys,
    loading,
    fetchApiKeyById,
    selectedKey,
    updateApiKey,
    deleteApiKey,
    error,
  } = useApiKeys(token);

  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const isSearchRequest = searchTerm.trim().length >= 3;

  const handleViewApiKey = async (id) => {
    try {
      await fetchApiKeyById(id);
      setShowDetailsDrawer(true);
    } catch (error) {
      toast.error(error, { theme: "colored" });
    }
  };

  const confirmDelete = (id) => {
    setKeyToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    await deleteApiKey(keyToDelete);
  }

  const filteredApiKeys = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const hasSearch = term.length >= 3;

    return (apiKeys || []).filter((item) => {
      const status = item?.active === true ? "ACTIVE" : "DISABLED";
      const byStatus = selectedStatus === "ALL" || selectedStatus === status;

      if (!hasSearch) return byStatus;

      const haystack = [
        item?.id,
        item?.name,
        item?.clientId,
        item?.key,
        status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return byStatus && haystack.includes(term);
    });
  }, [apiKeys, searchTerm, selectedStatus]);

  const totalElements = filteredApiKeys.length;
  const totalPages = totalElements > 0 ? Math.ceil(totalElements / pageSize) : 0;

  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [page, totalPages]);

  const formatKey = (key) => `${key.slice(0, 6)}...${key.slice(-4)}`;
  const {formatDateTime} = useFormattedDate();
  const formatText = (text) => `${text.slice(0, 15)}...`;
  const pillBase = {
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "0.7rem",
    fontWeight: 600,
    lineHeight: 1.2,
    border: "1px solid",
    cursor: "default",
    display: "inline-block",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  };

  const getStatusStyle = (isActive) => {
    const style = isActive
      ? { bg: "#e8f7e3", text: "#2e7d32", border: "#d5ebd0" }
      : { bg: "#fbe9e7", text: "#c62828", border: "#f5c6cb" };
    return {
      ...pillBase,
      backgroundColor: style.bg,
      color: style.text,
      borderColor: style.border,
    };
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ getValue }) => (
        <span className="text-900" style={{ fontSize: "0.75rem", fontWeight: 600 }}>
          {getValue()}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "clientId",
      header: "Client ID",
      cell: ({ getValue }) => (
        <span className="text-900 font-monospace" style={{ fontSize: "0.75rem" }}>
          {formatKey(getValue())}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "key",
      header: "Key",
      cell: ({ getValue }) => (
        <span className="text-900 font-monospace" style={{ fontSize: "0.75rem" }}>
          {formatKey(getValue())}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const content = value === true ? "ACTIVE" : "DISABLED";
        return <span style={getStatusStyle(value === true)}>{content}</span>;
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
    {
      accessorKey: "expiresAt",
      header: "Expires At",
      cell: ({ getValue }) => (
        <span className="text-500 d-inline-block" style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}>
          {formatDateTime(getValue())}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => (
        <span className="text-900 d-inline-block" style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}>
          {formatText(getValue())}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item onClick={() => handleViewApiKey(item.id)}>
                View/edit
              </Dropdown.Item>
              <Dropdown.Divider as="div" />
              <Dropdown.Item
                onClick={() => confirmDelete(item.id)}
                className="text-danger"
              >
                Delete
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const paginatedApiKeys = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;
    return filteredApiKeys.slice(start, end);
  }, [filteredApiKeys, page, pageSize]);

  const table = useAdvanceTable({
    data: paginatedApiKeys,
    columns,
    sortable: true,
    selection: true,
    pagination: false,
    perPage: pageSize,
  });

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading style={{ fontSize: 16 }}>
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
          Error
        </Alert.Heading>
        <p style={{ fontSize: "13.33px" }}>{error.message}</p>
      </Alert>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="layer-group"
        title="API Keys"
        subtitle="API Keys from the server for monitoring and management."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <AdvanceTableProvider {...table}>
        <Row
          className="mb-3 align-items-end gx-3 p-2 rounded bg-light mx-auto"
          style={{ maxWidth: 980 }}
        >
          <Col md={6}>
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F50E} Search API Keys"}
            </div>
            <Input
              placeholder='Search by "name", "id", or "clientId" (min. 3 chars)...'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              allowClear
              style={{
                width: "63%",
                borderRadius: 24,
                borderColor:
                  searchTerm.length > 0 && searchTerm.length < 3 ? "#ffa940" : "",
              }}
              suffix={
                loading && isSearchRequest ? (
                  <Spinner animation="border" size="sm" variant="success" />
                ) : (
                  searchTerm.length > 0 &&
                  searchTerm.length < 3 && (
                    <small className="text-warning" style={{ fontSize: "10px" }}>
                      Type {3 - searchTerm.length} more...
                    </small>
                  )
                )
              }
            />
          </Col>
          <Col md={2}>
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F3F7}\uFE0F Status Filter"}
            </div>
            <Select
              style={{ width: "100%" }}
              value={selectedStatus}
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { minWidth: 220 } } }}
              onChange={(value) => {
                setSelectedStatus(value || "ALL");
                setPage(0);
              }}
              options={[
                { label: "\u{1F310} ALL", value: "ALL" },
                { label: "\u2705 ACTIVE", value: "ACTIVE" },
                { label: "\u26D4 DISABLED", value: "DISABLED" },
              ]}
            />
          </Col>
        </Row>

        {loading && totalElements === 0 ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 200 }}
          >
            <Spinner animation="border" variant="success" />
          </div>
        ) : totalElements === 0 ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 200 }}
          >
            <Empty
              description={
                isSearchRequest && selectedStatus !== "ALL"
                  ? `No API keys found for "${searchTerm.trim()}" with status "${selectedStatus}".`
                  : isSearchRequest
                  ? `No API keys found for "${searchTerm.trim()}".`
                  : selectedStatus !== "ALL"
                  ? `No API keys found with status "${selectedStatus}".`
                  : "No API keys were found"
              }
            />
          </div>
        ) : (
          <>
            <AdvanceTable
              headerClassName="bg-300 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                bordered: true,
                striped: true,
                className: "fs-10 mb-0 overflow-hidden",
              }}
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={page > 0 && totalElements > 0 ? () => setPage(page - 1) : undefined}
                onNext={
                  page < totalPages - 1 && totalElements > 0
                    ? () => setPage(page + 1)
                    : undefined
                }
                pageSize={pageSize}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                pageSizeOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </>
        )}
      </AdvanceTableProvider>

      <ApiKeyDetailsDrawer
        selectedKey={selectedKey}
        loading={loading}
        showDrawer={showDetailsDrawer}
        setShowDrawer={setShowDetailsDrawer}
        updateApiKey={updateApiKey}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        itemName="api key"
        loading={loading}       
      />
    </>
  );
};
